/* eslint-disable no-param-reassign */
import { useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import filesize from "filesize";
import { intlShape } from "../../shapes";

import {
  isNotEmpty,
  scaleImageFile,
  iconForFileName,
  isEmpty,
} from "../../utils";
import { handleFormikValueChange } from "../../utils/formikUtils";

import AlertType from "../../constants/AlertType";
import InputAlert from "../Form/InputAlert";
import { withIntl } from "../../wrappers";
import { ActionButton } from "../ActionBar";

type OwnProps = {
  intl: intlShape;
  dropzoneInfoPrefix?: string;
  classNamePrefix?: string;
  multiple?: boolean;
  fileSizeLimit?: number;
  subscriptionStorageLimit?: number;
  currentSubscriptionStorage?: number;
  onDrop?: $TSFixMeFunction;
  name?: string;
  errors?: $TSFixMe;
  values?: $TSFixMe;
  buildInWhiteList?: string;
  whiteList?: string[];
};

const defaultProps: Partial<OwnProps> = {
  classNamePrefix: "files",
  multiple: true,
  dropzoneInfoPrefix: "support.ticket.create",
  whiteList: [],
};

type Props = OwnProps & typeof defaultProps;

export function FilesUpload(props: Props) {
  const {
    intl,
    whiteList,
    dropzoneInfoPrefix,
    fileSizeLimit,
    classNamePrefix,
    onDrop,
    multiple,
    buildInWhiteList,
    subscriptionStorageLimit,
    currentSubscriptionStorage,
    name,
    errors,
    values,
  } = props;
  const [rejectedList, setRejectedList] = useState([]);
  const [acceptedList, setAcceptedList] = useState([]);

  const onDropDefault = (acceptedFiles: $TSFixMe, rejectedFiles: $TSFixMe) => {
    let allRejectedFiles = isNotEmpty(rejectedFiles) ? rejectedFiles : [];
    const allAcceptedFiles = isNotEmpty(acceptedFiles) ? acceptedFiles : [];

    setRejectedList(allRejectedFiles);
    setAcceptedList(allAcceptedFiles);

    let promiseResults: $TSFixMe = [];
    let filesArray: $TSFixMe = [];
    let includedFiles: $TSFixMe = [];
    if (multiple) {
      const previouslyAcceptedFiles =
        // @ts-expect-error TS(2538) FIXME: Type 'undefined' cannot be used as an index type.
        values && values[props.name]?.acceptedFiles;
      includedFiles = isNotEmpty(previouslyAcceptedFiles)
        ? previouslyAcceptedFiles
        : [];
    }

    if (isNotEmpty(acceptedFiles)) {
      acceptedFiles.forEach((file: $TSFixMe) => {
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        });
        promiseResults = promiseResults.concat(
          scaleImageFile(file, 1200, 0.8).then((compressedFile) => {
            filesArray = filesArray.concat(compressedFile);
          }),
        );
      });
      Promise.all(promiseResults)
        .then(() => {
          acceptedFiles.forEach((file: $TSFixMe, index: $TSFixMe) => {
            if (
              // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
              whiteList.length > 0 &&
              // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
              !whiteList.find((suffix) =>
                file.name.toLowerCase().endsWith(suffix),
              )
            ) {
              file.uploadError = "incompatible_type_error";
              allRejectedFiles = allRejectedFiles.concat(file);
            } else if (
              isNotEmpty(filesArray) &&
              fileSizeLimit &&
              filesArray[index].size > Number(fileSizeLimit)
            ) {
              file.uploadError = "size_limit_exceeded";
              allRejectedFiles = allRejectedFiles.concat(file);
            } else if (
              isNotEmpty(filesArray) &&
              subscriptionStorageLimit &&
              currentSubscriptionStorage &&
              filesArray[index].size >
                Number(subscriptionStorageLimit) -
                  Number(currentSubscriptionStorage)
            ) {
              file.uploadError = "subscription_storage_limit_exceeded";
              allRejectedFiles = allRejectedFiles.concat(file);
            } else {
              includedFiles = includedFiles.concat(file);
            }
          });
        })
        .then(() => {
          setAcceptedList(includedFiles);
          setRejectedList(allRejectedFiles);
          handleFormikValueChange(props, {
            acceptedFiles: includedFiles,
            rejectedFiles: allRejectedFiles,
          });
        });
    }
  };

  const { getRootProps, getInputProps, isDragActive, acceptedFiles } =
    useDropzone({
      accept: buildInWhiteList,
      onDrop: onDrop || onDropDefault,
      multiple,
    });

  const onDeleteDefault = () => {
    acceptedFiles.splice(-1, 1);
    acceptedList.splice(-1, 1);
    setAcceptedList(acceptedList);
    setRejectedList([]);
    handleFormikValueChange(props, {
      acceptedFiles: acceptedList,
      rejectedFiles: rejectedList,
    });
  };
  const rejectedFilesRendered = rejectedList.map((file) => {
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    let whiteListReadable = `( ${whiteList.join(", ")} )`;
    if (isEmpty(whiteList)) {
      // @ts-expect-error TS(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
      whiteListReadable = buildInWhiteList;
    }

    if (isEmpty((file as $TSFixMe).name) && (file as $TSFixMe).file) {
      // only needed when the file is rejected directly from the dropzone (e.g. because of type)
      // the name variant is only needed for cypress headless browser
      /* istanbul ignore next */
      (file as $TSFixMe).name = isNotEmpty((file as $TSFixMe).file.path)
        ? (file as $TSFixMe).file.path
        : (file as $TSFixMe).file.name;
    }

    switch ((file as $TSFixMe).uploadError) {
      case "size_limit_exceeded":
        (file as $TSFixMe).uploadErrorMessage = intl.formatMessage(
          { id: "dropzone.size_error" },
          // @ts-expect-error TS(2345) FIXME: Argument of type 'number | undefined' is not assig... Remove this comment to see the full error message
          { limit: filesize(fileSizeLimit) },
        );
        break;
      case "subscription_storage_limit_exceeded":
        (file as $TSFixMe).uploadErrorMessage = intl.formatMessage({
          id: "dropzone.subscription_storage_limit_exceeded",
        });
        break;
      case "incompatible_type_error":
        (file as $TSFixMe).uploadErrorMessage = intl.formatMessage(
          { id: "dropzone.incompatible_type_error" },
          { whitelist: whiteListReadable },
        );
        break;
      default:
        (file as $TSFixMe).uploadErrorMessage = intl.formatMessage(
          { id: "dropzone.incompatible_type_error" },
          { whitelist: whiteListReadable },
        );
    }

    // prettier-ignore
    return (<li id="rejected-file" key={(file as $TSFixMe).name}>
          <strong>{(file as $TSFixMe).name}</strong>
          :
          {' '}
          {(file as $TSFixMe).uploadErrorMessage}
        </li>);
  });

  let rejectionError = null;
  // @ts-expect-error TS(2538) FIXME: Type 'undefined' cannot be used as an index type.
  if (errors && errors[name]) {
    rejectionError = (
      <div id="rejection-input-error" className="files-rejection-full-width">
        {/* @ts-expect-error TS(2538) FIXME: Type 'undefined' cannot be used as an index type. */}
        <InputAlert message={errors[name]} type={AlertType.ERROR} />
      </div>
    );
  }
  if (rejectedList && rejectedList.length > 0) {
    if (multiple) {
      rejectionError = (
        <div id="rejection-input-alert" className="files-rejection-full-width">
          <InputAlert
            message={intl.formatMessage({ id: "dropzone.rejection" })}
            type={AlertType.ERROR}
          >
            <ul>{rejectedFilesRendered}</ul>
          </InputAlert>
        </div>
      );
    } else if (rejectedList.length > 1) {
      // this happens if the user drop multiple files with multiple false dropzone
      rejectionError = (
        <div id="rejection-input-alert" className="files-rejection-full-width">
          <ul>
            <li>
              {intl.formatMessage({ id: "dropzone.mutiple_drops_forbidden" })}
            </li>
          </ul>
        </div>
      );
    } else {
      rejectionError = (
        <div id="rejection-input-alert" className="files-rejection-full-width">
          <ul>{rejectedFilesRendered}</ul>
        </div>
      );
    }
  }

  const style = useMemo(
    () => ({
      ...{ className: `${classNamePrefix}-upload` },
      ...(isDragActive
        ? { className: `${classNamePrefix}-upload-active` }
        : {}),
    }),
    [isDragActive],
  );

  const dropzone =
    !multiple && acceptedList.length === 1 ? (
      <div id="single-file-selected" className="edit-files">
        <div className="file-delete">
          <ActionButton
            id="delete-file"
            icon="icon-eh-delete"
            defaultLabel={intl.formatMessage({ id: "button.delete" })}
            onClick={onDeleteDefault}
          />
        </div>
        <div className="files-dropzone">
          <div className="file-selected">
            <div
              className={`icon ${iconForFileName((acceptedList[0] as $TSFixMe).name)}`}
            />
            <p>{(acceptedList[0] as $TSFixMe).name}</p>
          </div>
        </div>
        {errors ? rejectionError : null}
      </div>
    ) : (
      <div>
        <section className={`${classNamePrefix}-dropzone`}>
          <div
            id={`${classNamePrefix}-dropzone-root`}
            {...getRootProps({ ...style })}
          >
            <input
              id={`${classNamePrefix}-dropzone-input`}
              data-testid={`${classNamePrefix}-dropzone-input`}
              {...getInputProps()}
            />
            <div className="icon lcm-iot-icon-upload" />
            <p id="dropzone-info">
              {intl.formatMessage({
                id: `${dropzoneInfoPrefix}.dropzone_info`,
              })}
            </p>
          </div>
        </section>
        {rejectionError}
      </div>
    );
  return dropzone;
}

FilesUpload.defaultProps = defaultProps;

export default withIntl(FilesUpload);
